import { useTranslate } from '@refinedev/core';
import { Alert, Form, Input, Radio, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import {
  SectionDrawer,
  ToolDatePicker,
  dateOfDay,
  dayOf,
  fmtDay,
} from '../../components/ToolSection';
import {
  emptyWindowDraft,
  fewNights,
  hoursMinutes,
  windowBasis,
  windowInput,
  windowProblem,
  type SleepWeekRow,
  type UsualRuleRow,
  type WindowDraft,
  type WindowRow,
} from './model';
import { useSleepCommand } from './useSleepCommand';

type Tx = (key: string, options?: Record<string, unknown>) => string;
type Locale = 'en' | 'ru';

export const protocolName = (p: 'cbti' | 'bbti') =>
  p === 'cbti' ? 'CBT-I' : 'BBTI';

/** "23:00 – 06:00 · from 15 Dec 2026 · CBT-I · set by Vladimir Babarykin" (the ending is the caller's). */
export const windowText = (w: WindowRow, t: Tx, locale: Locale): string =>
  [
    t('sleep.hub.window.current', {
      bedtime: w.bedtime,
      riseTime: w.riseTime,
      date: fmtDay(w.effectiveFrom, locale),
      protocol: protocolName(w.protocol),
    }),
    w.performerName ? t('sleep.hub.window.setBy', { name: w.performerName }) : '',
  ]
    .filter(Boolean)
    .join(' · ');

/** The usual rule as one sentence (for the therapist only; "you decide"). */
export const usualRuleText = (rule: UsualRuleRow, t: Tx): string => {
  const action = t(
    `sleep.hub.usualRule.${rule.atFloor ? 'atFloor' : rule.action}`,
  );
  return rule.protocol === 'cbti'
    ? t('sleep.hub.usualRule.cbti', {
        efficiency: rule.meanEfficiency ?? '–',
        nights: rule.nights,
        action,
      })
    : t('sleep.hub.usualRule.bbti', {
        fellAsleep: rule.meanFellAsleep ?? '–',
        awake: rule.meanAwakeInNight ?? '–',
        nights: rule.nights,
        action,
      });
};

/**
 * E45 · "Set a new window" — a side drawer on the Sleep window card (design §2.1 (d)2). The start date can no longer be empty (it starts as
 * today and cannot be cleared), and any missing field gives a LASTING, specific error in the drawer (N-4 (4)).
 */
export const SetWindowDrawer = ({
  open,
  clientId,
  view,
  locale,
  onClose,
  onSaved,
}: {
  open: boolean;
  clientId: string;
  view: SleepWeekRow | undefined;
  locale: Locale;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const t = useTranslate() as unknown as Tx;
  const { run, isLoading } = useSleepCommand();
  const today = dayOf(new Date());
  const [win, setWin] = useState<WindowDraft>(() =>
    emptyWindowDraft('cbti', today),
  );
  const [initial, setInitial] = useState<WindowDraft>(win);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    const fresh = emptyWindowDraft('cbti', dayOf(new Date()));
    setWin(fresh);
    setInitial(fresh);
    setError(null);
  }, [open]);

  const patch = (p: Partial<WindowDraft>) => {
    setError(null);
    setWin((d) => ({ ...d, ...p }));
  };
  const hm = (m: number) => t('sleep.units.hoursMinutes', hoursMinutes(m));
  const basis = view ? windowBasis(view, hm) : null;
  const current = view?.window && !view.window.endedAt ? view.window : null;
  const rule = view?.usualRule;

  const onSave = async () => {
    const problem = windowProblem(win);
    if (problem) {
      setError(t(`sleep.hub.window.${problem}`));
      return;
    }
    const input = windowInput(win, clientId, basis);
    if (!input) {
      setError(t('sleep.hub.window.error'));
      return;
    }
    const res = await run(
      'setSleepWindow',
      input as unknown as Record<string, unknown>,
      t('sleep.hub.window.error'),
    );
    if (res.ok) onSaved();
    else setError(res.message);
  };

  return (
    <SectionDrawer
      open={open}
      title={t('sleep.hub.window.title')}
      testId="sleep-hub-set-window"
      saveTestId="sleep-hub-window-set"
      context={
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Alert type="info" showIcon message={t('sleep.hub.window.screening')} />
          {view && fewNights(view) ? (
            <Alert
              type="warning"
              showIcon
              message={t('sleep.hub.window.fewNights')}
              data-testid="sleep-hub-window-few"
            />
          ) : null}
          {basis ? (
            <Typography.Text
              type="secondary"
              data-testid="sleep-hub-window-basis"
            >
              {t('sleep.hub.window.basis', {
                nights: basis.nights,
                asleep: basis.meanTotalSleep,
                efficiency: basis.meanEfficiency,
              })}
            </Typography.Text>
          ) : null}
          {rule ? (
            <Typography.Text type="secondary">
              {`${t('sleep.hub.usualRule.forYou')} ${usualRuleText(rule, t)}`}
            </Typography.Text>
          ) : null}
          {current ? (
            <Typography.Text>
              {t('sleep.hub.window.replaces', {
                window: `${current.bedtime} – ${current.riseTime} · ${fmtDay(
                  current.effectiveFrom,
                  locale,
                )}`,
              })}
            </Typography.Text>
          ) : null}
        </Space>
      }
      saveLabel={t('sleep.hub.window.set')}
      saving={isLoading}
      error={error}
      dirty={JSON.stringify(win) !== JSON.stringify(initial)}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Form layout="vertical" component="div">
        <Form.Item label={t('sleep.hub.window.bedtime')}>
          <Input
            placeholder="23:30"
            value={win.bedtime}
            onChange={(e) => patch({ bedtime: e.target.value })}
            data-testid="sleep-hub-window-bedtime"
          />
        </Form.Item>
        <Form.Item label={t('sleep.hub.window.riseTime')}>
          <Input
            placeholder="06:00"
            value={win.riseTime}
            onChange={(e) => patch({ riseTime: e.target.value })}
            data-testid="sleep-hub-window-riseTime"
          />
        </Form.Item>
        <Form.Item label={t('sleep.hub.window.effectiveFrom')}>
          <ToolDatePicker
            value={win.effectiveFrom ? dateOfDay(win.effectiveFrom) : null}
            onChange={(d) => patch({ effectiveFrom: d ? dayOf(d) : '' })}
            testId="sleep-hub-window-effectiveFrom"
          />
        </Form.Item>
        <Form.Item label={t('sleep.hub.window.protocol')}>
          <Radio.Group
            value={win.protocol}
            onChange={(e) => patch({ protocol: e.target.value })}
            data-testid="sleep-hub-window-protocol"
          >
            <Space direction="vertical">
              <Radio value="cbti" data-testid="sleep-hub-window-protocol-cbti">
                {t('sleep.hub.window.cbti')}
              </Radio>
              <Radio value="bbti" data-testid="sleep-hub-window-protocol-bbti">
                {t('sleep.hub.window.bbti')}
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item label={t('sleep.hub.window.note')}>
          <Input.TextArea
            value={win.note}
            onChange={(e) => patch({ note: e.target.value })}
            rows={2}
            maxLength={1000}
            data-testid="sleep-hub-window-note"
          />
        </Form.Item>
      </Form>
    </SectionDrawer>
  );
};

/** E45 · "End the window" — a danger drawer on the Sleep window card; the button exists only while a window is current or upcoming. */
export const EndWindowDrawer = ({
  open,
  clientId,
  window: w,
  locale,
  onClose,
  onSaved,
}: {
  open: boolean;
  clientId: string;
  window: WindowRow | null;
  locale: Locale;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const t = useTranslate() as unknown as Tx;
  const { run, isLoading } = useSleepCommand();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const onSave = async () => {
    if (!w) return;
    const res = await run(
      'clearSleepWindow',
      { subject: clientId, windowId: w.windowId, reason: reason.trim() || null },
      t('sleep.hub.window.end.error'),
    );
    if (res.ok) onSaved();
    else setError(res.message);
  };

  return (
    <SectionDrawer
      open={open}
      title={t('sleep.hub.window.end.title')}
      testId="sleep-hub-end-window"
      saveTestId="sleep-hub-end-window-action"
      danger
      context={
        <Space direction="vertical" size={4}>
          {w ? <Typography.Text>{windowText(w, t, locale)}</Typography.Text> : null}
          <Typography.Text type="secondary">
            {t('sleep.hub.window.end.body')}
          </Typography.Text>
        </Space>
      }
      saveLabel={t('sleep.hub.window.end.action')}
      saving={isLoading}
      error={error}
      dirty={reason.trim() !== ''}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Form layout="vertical" component="div">
        <Form.Item label={t('sleep.hub.window.end.reason')}>
          <Input
            value={reason}
            onChange={(e) => {
              setError(null);
              setReason(e.target.value);
            }}
            data-testid="sleep-hub-end-reason"
          />
        </Form.Item>
      </Form>
    </SectionDrawer>
  );
};
